import { useEffect, useRef, useState } from "react";
import { Loader2, Pencil, Eye, Save, Sparkles, ShieldAlert, FileDown } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/context/AppContext";
import { API, api, fmtTime, streamSSE, withPersona, safe, exportGuard } from "@/lib/api";
import { RichText } from "@/components/meridian/RichText";

export function MemoDraft({ task, autoGenerate, onStarted, reload }) {
  const { persona } = useApp();
  const [content, setContent] = useState(task.memo?.content || "");
  const [streaming, setStreaming] = useState(false);
  const [editing, setEditing] = useState(false);

  useEffect(() => { if (!streaming) setContent(task.memo?.content || ""); }, [task.memo, streaming]);

  const generate = async () => {
    setStreaming(true); setEditing(false); setContent("");
    try {
      await streamSSE(`/tasks/${task.id}/memo`, {}, (ev) => {
        if (ev.type === "delta") setContent((c) => c + ev.content);
        if (ev.type === "error") toast.error(ev.content);
      });
      await reload();
    } catch (e) { if (!String(e.message).startsWith("Blocked")) toast.error(e.message?.startsWith("Run extraction") ? e.message : "Memo generation failed - retry"); }
    setStreaming(false);
  };
  const started = useRef(false);
  useEffect(() => {
    if (autoGenerate && !started.current) { started.current = true; onStarted(); generate().finally(() => { started.current = false; }); }
  }, [autoGenerate]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = safe(async () => {
    await api.put(`/tasks/${task.id}/memo`, { content });
    await reload(); setEditing(false); toast.success("Draft saved - still requires human review");
  });

  return (
    <div className="p-6 max-w-4xl" data-testid="memo-draft">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <span data-testid="memo-review-badge" className="flex items-center gap-1.5 text-[11px] px-2 py-1 rounded border border-amber-500/50 text-amber-700 bg-amber-500/10"><ShieldAlert className="w-3.5 h-3.5" />Draft - requires human review</span>
          {task.memo?.generated_at && <span className="text-[10px] num text-slate-500">generated {fmtTime(task.memo.generated_at)}{task.memo.edited ? " · edited" : ""}</span>}
        </div>
        <div className="flex gap-2">
          {content && !streaming && task.memo?.content && (
            <a data-testid="memo-export-pdf" onClick={exportGuard(persona, "pack.export", `/tasks/${task.id}/memo.pdf`)} href={withPersona(`${API}/tasks/${task.id}/memo.pdf`)} target="_blank" rel="noreferrer" className="text-xs flex items-center gap-1.5 px-2.5 py-1.5 rounded border border-sky-500/50 text-sky-700 hover:bg-sky-600/10 transition-colors">
              <FileDown className="w-3.5 h-3.5" />Export PDF
            </a>
          )}
          {content && !streaming && (
            <button data-testid="memo-edit-toggle" onClick={() => setEditing((e) => !e)} className="text-xs flex items-center gap-1.5 px-2.5 py-1.5 rounded border border-slate-300 text-slate-700 hover:border-sky-500/50 transition-colors">
              {editing ? <><Eye className="w-3.5 h-3.5" />Preview</> : <><Pencil className="w-3.5 h-3.5" />Edit</>}
            </button>
          )}
          {editing && <button data-testid="memo-save-button" onClick={save} className="text-xs flex items-center gap-1.5 px-2.5 py-1.5 rounded border border-emerald-500/50 text-emerald-700 hover:bg-emerald-500/10 transition-colors"><Save className="w-3.5 h-3.5" />Save draft</button>}
          <button data-testid="memo-generate-button" disabled={streaming} onClick={generate} className="text-xs flex items-center gap-1.5 px-3 py-1.5 rounded bg-sky-600 text-white hover:bg-sky-700 transition-colors disabled:opacity-50">
            {streaming ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}{content ? "Regenerate" : "Generate credit memo"}
          </button>
        </div>
      </div>
      {editing ? (
        <textarea data-testid="memo-editor" value={content} onChange={(e) => setContent(e.target.value)} className="w-full h-[60vh] bg-slate-50 border border-slate-300 rounded p-4 num text-xs text-slate-800 outline-none focus:border-sky-500/60" />
      ) : content ? (
        <div className="panel p-6" data-testid="memo-content"><RichText text={content} metrics={task.metrics} />{streaming && <span className="inline-block w-2 h-4 bg-sky-700 animate-pulse align-middle" />}</div>
      ) : (
        <div className="panel p-10 text-sm text-slate-500 grid-bg">{streaming ? "Drafting…" : "No memo yet. Generate a draft from the extracted facts, calculations and records - every figure will carry a clickable citation."}</div>
      )}
    </div>
  );
}
